import { inject, Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AppService } from '../app.service';
import { FeaturesEnum } from 'src/app/@shared/enums/features.enum';

/** Clé de `localStorage` de l'état ouvert / fermé du panneau. */
const OPEN_STORAGE_KEY = 'assistantPanelOpen';

/** En dessous de cette largeur (lg), le panneau passe par-dessus l'écran, en tiroir. */
const DRAWER_QUERY = '(max-width: 1023.98px)';

/** À cette largeur (dxl) et en dessous, l'ouverture du panneau réduit le menu latéral. */
const COMPACT_MENU_QUERY = '(max-width: 1440px)';

/** La page pleine largeur de l'assistant, sous le segment du dossier. */
const FULL_PAGE_URL = /^\/c\/[^/]+\/assistant(\/|$)/;

/**
 * État du panneau permanent de l'assistant IA (#1292).
 *
 * Le panneau est hébergé par le shell, hors du `router-outlet` : la conversation et son flux
 * vivent dans `AiAssistantService` et ne dépendent donc d'aucune route. Ce service ne porte
 * que la présentation : ouvert ou fermé (retenu dans le navigateur), page pleine largeur
 * affichée ou non, tiroir ou colonne, menu latéral réduit à l'ouverture.
 */
@Injectable({
    providedIn: 'root'
})
export class AssistantPanelService {

    private _appService = inject(AppService);
    private _breakpointObserver = inject(BreakpointObserver);
    private _router = inject(Router);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _compactViewport: boolean = false;
    private _drawer: boolean = false;
    private _fullPage: boolean = false;
    /** Le menu a été refermé par l'ouverture du panneau : c'est à nous de le rouvrir. */
    private _menuClosedByPanel: boolean = false;
    private _open: boolean = localStorage.getItem(OPEN_STORAGE_KEY) === 'true';
    /** Écran quitté par « Agrandir », où « Réduire » ramène. */
    private _returnUrl: string | null = null;

    constructor() {
        this._fullPage = this._isFullPageUrl(this._router.url);

        this._router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this._fullPage = this._isFullPageUrl(this._router.url);
                this._syncMenu();
            });
        this._breakpointObserver.observe(DRAWER_QUERY).subscribe(state => {
            this._drawer = state.matches;
            this._syncMenu();
        });
        this._breakpointObserver.observe(COMPACT_MENU_QUERY).subscribe(state => {
            this._compactViewport = state.matches;
            this._syncMenu();
        });
        this._appService.clientSelected$.subscribe(() => this._syncMenu());
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** Le panneau passe par-dessus l'écran au lieu de prendre une colonne. */
    public get drawer(): boolean {
        return this._drawer;
    }

    /** La page pleine largeur est affichée : le panneau ne l'est pas. */
    public get fullPage(): boolean {
        return this._fullPage;
    }

    /** L'état ouvert ou fermé retenu, que le panneau soit affiché ou non. */
    public get open(): boolean {
        return this._open;
    }

    /** Le panneau est à l'écran : ouvert, hors page pleine largeur, dossier équipé de l'assistant. */
    public get visible(): boolean {
        return this._open && !this._fullPage && this._hasAssistant();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public close(): void {
        this._setOpen(false);
    }

    /** Ouvre la conversation en pleine page, depuis l'écran courant, dont « Réduire » ramène. */
    public expand(sessionId: string | null): void {
        this.rememberReturnUrl();
        const path = sessionId ? `/assistant/session/${sessionId}` : '/assistant';
        this._router.navigateByUrl(this._appService.clientUrl(path));
    }

    /** Retient l'écran courant, où « Réduire » ramène — pour une pleine page ouverte par un lien. */
    public rememberReturnUrl(): void {
        if (!this._fullPage) {
            this._returnUrl = this._router.url;
        }
    }

    /** Quitte la pleine page : retour à l'écran d'où l'on vient, panneau ouvert. */
    public reduce(): void {
        const url = this._returnUrl ?? this._appService.clientUrl('/dashboard');
        this._returnUrl = null;
        this._setOpen(true);
        this._router.navigateByUrl(url);
    }

    public toggle(): void {
        this._setOpen(!this._open);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _hasAssistant(): boolean {
        return !!this._appService.selectedClient?.hasFeature(FeaturesEnum.AiAgent);
    }

    private _isFullPageUrl(url: string): boolean {
        return FULL_PAGE_URL.test(url.split(/[?#]/)[0]);
    }

    private _setOpen(open: boolean): void {
        this._open = open;
        localStorage.setItem(OPEN_STORAGE_KEY, open.toString());
        this._syncMenu();
    }

    /**
     * Sur un écran étroit, le panneau en colonne laisse trop peu au centre : le menu latéral
     * passe en mode réduit tant qu'il est affiché, puis reprend sa place à sa fermeture. Le
     * tiroir, lui, recouvre l'écran sans le rétrécir. La réduction n'est pas retenue dans le
     * navigateur : seul le geste de l'utilisateur sur le menu l'est.
     */
    private _syncMenu(): void {
        const compact = this.visible && !this._drawer && this._compactViewport;
        if (compact && this._appService.menuOpen && !this._menuClosedByPanel) {
            this._appService.menuOpen = false;
            this._menuClosedByPanel = true;
        } else if (!compact && this._menuClosedByPanel) {
            this._menuClosedByPanel = false;
            this._appService.menuOpen = true;
        }
    }
}
